// Phase 32 Plan 32-05 Task 1 — MulliganSelector (RUNE-35).
//
// Full-screen overlay for the Lexical Mulligan power-up: lets the caller pick
// up to 6 standard cells to re-roll. Anchors + Hollows are ineligible (UI-SPEC
// §6 + CONTEXT D-04). On Confirm, dispatches setPhaseB({mode:'lexical_mulligan',
// payload:{cell_indices}}) and calls onSubmit. On Cancel / Escape / tap-outside
// (NOT applicable here — the overlay is full-screen and the field stays
// visible underneath, so there is no "outside"), dispatches onDismiss.
//
// Composition (UI-SPEC §6):
//   - role="dialog" + aria-modal=true backdrop wrapper (NOT scrimmed — the
//     field cells stay visible at full opacity per UI-SPEC §6 lock).
//   - Floating counter pill at top: "X of 6 selected". Background turns
//     Sun-Yellow at count === 6.
//   - DOM cell grid: renders one button per board cell. Eligible cells show
//     a Sun-Yellow border on tap; disabled cells (Anchor/Hollow) render a
//     50% bg-deep scrim + lock-icon overlay; their pointerdown triggers a
//     150ms shake (or 200ms tint flash under reduced motion) + assertive SR
//     announce.
//   - Bottom action bar: Cancel (Btn ghost-equivalent) + Confirm (Btn primary)
//     with plural-aware "Re-roll {N} {rune|runes}" label. Confirm disabled at
//     count === 0.
//
// State machine (UI-SPEC §6):
//   - `selecting`           default. taps toggle membership in `selected` set.
//   - `disabled-tap`        ephemeral. fires when user taps Anchor/Hollow.
//   - `selection-full`      count === 6.
//   - `selection-full-rejected`  count === 6 AND user taps an unselected
//                                eligible cell. Set unchanged; visual flash.
//   Tracked via the returned outcome from `toggle(idx, isDisabled)`:
//   'ok' | 'disabled' | 'rejected'.
//
// A11y contract (UI-SPEC §9):
//   - role="dialog" + aria-modal=true + aria-labelledby + aria-describedby.
//   - useFocusTrap (Escape dismiss + Tab cycling + initial focus + restore).
//   - announce(a11y.announce.mulligan_open, polite) on mount.
//   - announce(match.mulligan.disabled_announce, assertive) on disabled tap.
//   - announce(match.mulligan.selection_full_announce, polite) on rejected tap.
//
// Locks:
//   - Pitfall 6 (selection cap): toggle() short-circuits at size === 6 &&
//     !has(idx). Counter never exceeds 6.
//   - T-32-04: NO browser-storage caching. Selection state is in-memory.
//   - T-32-05 / B12 firewall: NO opponent private-field reads. The overlay
//     reads ONLY playerView.board (public-by-construction).
//   - Tweedle Pitfall 5: overlay is pure DOM, no private-Group instantiation
//     (shake is a CSS @keyframes animation triggered by a transient class —
//     keeps the overlay self-contained).
//   - DEC-024: lexical_mulligan stays in the 8-power-up set; payload shape
//     `{ cell_indices: number[] }` mirrors the server contract per
//     apps/backend/src/match/powerup/lexical-mulligan.ts.

import { useStore } from '@nanostores/react'
import { type JSX, useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import type { PlayerView } from '../../game/PlayerViewSchema'
import { $reducedMotion } from '../../stores/motion'
import { $mulliganActive } from '../../stores/mulligan-active'
import { Btn } from '../components/Btn'
import { useAnnounce } from '../components/useAnnounce'
import { useFocusTrap } from '../components/useFocusTrap'

/** Maximum number of cells selectable for a single Mulligan re-roll. Per
 *  UI-SPEC §6 + Pitfall 6 — server-side validator (Phase 28 Plan 28-03) is
 *  the trust boundary; client UX prevents the bad case from ever submitting. */
export const MAX_SELECT = 6

export interface MulliganSelectorProps {
  readonly playerView: PlayerView
  readonly onSubmit: (cellIndices: ReadonlyArray<number>) => void
  readonly onDismiss: () => void
}

type Outcome = 'ok' | 'disabled' | 'rejected'

/** A cell is eligible iff it is neither an Anchor nor a Hollow. */
function isCellDisabled(c: PlayerView['board'][number]): boolean {
  return c.is_anchor === true || c.is_hollow === true
}

/** Selection-state hook — per UI-SPEC §6 FSM + Pitfall 6 selection cap.
 *
 * `toggle()` returns the outcome synchronously by inspecting the CURRENT
 * state (not via the setSelected updater) — necessary because the React 18
 * batched state updater may not run inline under test environments, and the
 * caller needs to fire the announce / shake side-effects in the same tick. */
function useMulliganSelection(): {
  selected: ReadonlySet<number>
  count: number
  toggle: (cellIdx: number, isDisabled: boolean) => Outcome
} {
  const [selected, setSelected] = useState<ReadonlySet<number>>(
    () => new Set<number>(),
  )

  const toggle = useCallback(
    (cellIdx: number, isDisabled: boolean): Outcome => {
      if (isDisabled) return 'disabled'
      // Decide outcome from the current state BEFORE scheduling the update.
      // Inside setSelected we mirror the same logic (the updater is the
      // source of truth for the next state; the synchronous outcome is what
      // we report to the caller for side-effects).
      let outcome: Outcome
      if (selected.has(cellIdx)) {
        outcome = 'ok'
        const next = new Set(selected)
        next.delete(cellIdx)
        setSelected(next)
      } else if (selected.size >= MAX_SELECT) {
        // Pitfall 6 — 7th tap on unselected cell. Reject without mutating.
        outcome = 'rejected'
      } else {
        outcome = 'ok'
        const next = new Set(selected)
        next.add(cellIdx)
        setSelected(next)
      }
      return outcome
    },
    [selected],
  )

  return { selected, count: selected.size, toggle }
}

export function MulliganSelector({
  playerView,
  onSubmit,
  onDismiss,
}: MulliganSelectorProps): JSX.Element {
  const intl = useIntl()
  const announce = useAnnounce()
  const reducedMotion = useStore($reducedMotion)
  const dialogRef = useRef<HTMLDivElement>(null)

  useFocusTrap(dialogRef, true, onDismiss)

  // Per-cell shake state (cellIdx → timeout id). When a disabled cell is tapped
  // we briefly add a CSS class that drives the shake (or tint-flash under
  // reduced motion). A single shake/flash per cell at a time; re-taps cancel
  // any prior in-flight transient and restart the visual cue.
  const [shakingCellIdx, setShakingCellIdx] = useState<number | null>(null)
  const shakeTimeoutRef = useRef<number | null>(null)
  // Rejected-tap ephemeral flag (count===6 + unselected tap) drives the
  // counter pill border flash visual.
  const [rejectedFlash, setRejectedFlash] = useState(false)
  const rejectedTimeoutRef = useRef<number | null>(null)

  const { selected, count, toggle } = useMulliganSelection()

  // Mount effect: announce + flip $mulliganActive.
  useEffect(() => {
    announce(
      intl.formatMessage({ id: 'a11y.announce.mulligan_open' }),
      'polite',
    )
    $mulliganActive.set(true)
    return () => {
      $mulliganActive.set(false)
      if (shakeTimeoutRef.current !== null) {
        window.clearTimeout(shakeTimeoutRef.current)
        shakeTimeoutRef.current = null
      }
      if (rejectedTimeoutRef.current !== null) {
        window.clearTimeout(rejectedTimeoutRef.current)
        rejectedTimeoutRef.current = null
      }
    }
  }, [announce, intl])

  const triggerShake = useCallback(
    (cellIdx: number) => {
      if (shakeTimeoutRef.current !== null) {
        window.clearTimeout(shakeTimeoutRef.current)
      }
      setShakingCellIdx(cellIdx)
      // Reduced-motion: 200ms tint flash; standard: 150ms shake.
      const duration = reducedMotion ? 200 : 150
      shakeTimeoutRef.current = window.setTimeout(() => {
        setShakingCellIdx(null)
        shakeTimeoutRef.current = null
      }, duration)
    },
    [reducedMotion],
  )

  const triggerRejectedFlash = useCallback(() => {
    if (rejectedTimeoutRef.current !== null) {
      window.clearTimeout(rejectedTimeoutRef.current)
    }
    setRejectedFlash(true)
    rejectedTimeoutRef.current = window.setTimeout(() => {
      setRejectedFlash(false)
      rejectedTimeoutRef.current = null
    }, 200)
  }, [])

  const handleCellTap = useCallback(
    (cell: PlayerView['board'][number]) => {
      const disabled = isCellDisabled(cell)
      const outcome = toggle(cell.cell_idx, disabled)
      if (outcome === 'disabled') {
        triggerShake(cell.cell_idx)
        announce(
          intl.formatMessage({ id: 'match.mulligan.disabled_announce' }),
          'assertive',
        )
      } else if (outcome === 'rejected') {
        triggerRejectedFlash()
        announce(
          intl.formatMessage({ id: 'match.mulligan.selection_full_announce' }),
          'polite',
        )
      }
    },
    [toggle, triggerShake, triggerRejectedFlash, announce, intl],
  )

  const handleConfirm = useCallback(() => {
    if (count === 0) return
    const sorted = Array.from(selected).sort((a, b) => a - b)
    onSubmit(sorted)
  }, [count, selected, onSubmit])

  const counterFull = count === MAX_SELECT

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mulligan-title"
      aria-describedby="mulligan-subtitle"
      ref={dialogRef}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        // NOT scrimmed (UI-SPEC §6 lock) — field cells stay visible. We use
        // a transparent click-blocker so the underlying field's normal
        // pointer handlers do not fire during Mulligan selection (the cell
        // grid below is the only interactive surface).
        background: 'transparent',
        pointerEvents: 'auto',
        zIndex: 50,
      }}
    >
      {/* Visually-hidden title + subtitle (referenced via aria-labelledby /
          aria-describedby). The visible title is the counter pill at the top. */}
      <h2
        id="mulligan-title"
        className="sr-only"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        <FormattedMessage id="match.mulligan.title" />
      </h2>
      <p
        id="mulligan-subtitle"
        className="sr-only"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        <FormattedMessage id="match.mulligan.subtitle" />
      </p>

      {/* Top counter pill — fixed position 80px from top safe-area per
          UI-SPEC §6 (= --space-6 × 2.5 exception, off-scale documented). */}
      <div
        data-mulligan-counter
        data-counter-full={counterFull ? 'true' : 'false'}
        data-rejected-flash={rejectedFlash ? 'true' : 'false'}
        style={{
          position: 'fixed',
          top: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: 'var(--space-2) var(--space-4)',
          background: counterFull
            ? 'var(--color-cta-primary)'
            : 'var(--color-surface-mid)',
          color: counterFull
            ? 'var(--color-inkwell)'
            : 'var(--color-text-primary)',
          border: rejectedFlash
            ? '2px solid var(--color-cta-destructive)'
            : '2px solid var(--color-surface-stroke)',
          borderRadius: 9999,
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-16)',
          boxShadow: 'var(--shadow-pop)',
          zIndex: 60,
        }}
      >
        <FormattedMessage id="match.mulligan.counter" values={{ count }} />
      </div>

      {/* Cell grid — DOM buttons over the playerView.board layout. Renders
          a 6-wide responsive grid; rows are derived from board.length / 6.
          Each cell is a button; eligibility-disabled cells render the
          50% scrim + lock icon overlay (UI-SPEC §6 disabled-cell visual). */}
      <div
        data-mulligan-grid
        style={{
          marginTop: 144, // 80 (pill top) + 32 (pill height incl padding) + var(--space-4)
          padding: 'var(--space-4)',
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: 'var(--space-2)',
          maxWidth: 480,
          width: '100%',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        {playerView.board.map((cell) => {
          const disabled = isCellDisabled(cell)
          const isSelected = selected.has(cell.cell_idx)
          const isShaking = shakingCellIdx === cell.cell_idx
          // Reduced motion: show red tint flash instead of CSS shake.
          const flashTint = isShaking && reducedMotion
          const shakeStandard = isShaking && !reducedMotion
          return (
            <button
              key={`mulligan-cell-${cell.cell_idx}`}
              type="button"
              data-mulligan-cell-idx={cell.cell_idx}
              data-mulligan-cell-disabled={disabled ? 'true' : 'false'}
              data-mulligan-cell-selected={isSelected ? 'true' : 'false'}
              data-mulligan-cell-shaking={isShaking ? 'true' : 'false'}
              onClick={() => handleCellTap(cell)}
              aria-label={
                disabled
                  ? intl.formatMessage({ id: 'match.mulligan.disabled_label' })
                  : `${cell.letter ?? ''}${isSelected ? ' (selected)' : ''}`
              }
              aria-pressed={isSelected ? true : false}
              style={{
                position: 'relative',
                aspectRatio: '1 / 1',
                padding: 0,
                background: 'var(--color-surface-mid)',
                color: 'var(--color-text-primary)',
                border: isSelected
                  ? '3px solid var(--color-cta-primary)'
                  : '2px solid var(--color-surface-stroke)',
                borderRadius: 'var(--radius-md)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-display)',
                fontWeight: 700,
                fontSize: 'var(--font-size-22)',
                transform:
                  isSelected && !reducedMotion ? 'scale(1.08)' : 'scale(1.0)',
                transition: reducedMotion
                  ? 'none'
                  : 'transform 100ms ease-out, border-color 100ms ease-out',
                // CSS animation `mulligan-shake` reused from globals.css. Falls
                // back to inline-style transform animation when CSS keyframe
                // is unavailable — we attach via animation property only when
                // shakeStandard is true.
                animation: shakeStandard
                  ? 'mulligan-shake 150ms ease-in-out'
                  : 'none',
              }}
            >
              {cell.letter ?? ''}
              {disabled ? (
                // 50% bg-deep scrim + lock icon overlay (UI-SPEC §6 — no
                // desaturation; theme color stays recognizable per CONTEXT
                // D-04). Lock icon is a unicode glyph fallback until the
                // atlas adds `pi_lock_small` for DOM consumption.
                <span
                  data-mulligan-lock-overlay
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--color-scrim)',
                    color: 'var(--color-text-secondary)',
                    fontSize: 'var(--font-size-14)',
                    borderRadius: 'inherit',
                  }}
                >
                  {'🔒'}
                </span>
              ) : null}
              {flashTint ? (
                // Reduced-motion shake fallback — red overlay at α=0.6 for
                // 200ms (UI-SPEC §6 + §8). Identical pattern to
                // LetterCubeSprite.flashHollowRejection lines 437-447.
                <span
                  data-mulligan-flash-tint
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'var(--color-cta-destructive)',
                    opacity: 0.6,
                    borderRadius: 'inherit',
                    pointerEvents: 'none',
                  }}
                />
              ) : null}
            </button>
          )
        })}
      </div>

      {/* Bottom action bar — fixed bottom; Cancel (ghost) + Confirm (primary). */}
      <div
        data-mulligan-action-bar
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          padding: 'var(--space-4) var(--space-4) var(--space-5)',
          background: 'var(--color-surface-mid)',
          display: 'flex',
          gap: 'var(--space-2)',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 60,
        }}
      >
        <Btn kind="ghost" size="md" onClick={onDismiss}>
          <FormattedMessage id="match.mulligan.cancel" />
        </Btn>
        <Btn
          kind="primary"
          size="lg"
          disabled={count === 0}
          onClick={handleConfirm}
        >
          <FormattedMessage id="match.mulligan.confirm" values={{ count }} />
        </Btn>
      </div>
    </div>
  )
}
